import { sumBy } from 'lodash';

import { AddOnPriceDetails, PageModel, PaidUpgrade } from 'types/expiryRenewal';

export type BasketPricing = {
  totalPrice: number;
  addOnTotal: number;
  wasTotal: number | null;
};

export type UpgradePricing = {
  price: number;
  discountedPrice: number;
  monthlyPrice: number;
  monthlyDiscountedPrice: number;
};

export type ResolvedAddOnPrice = {
  nowPrice: number;
  // Only set when a discount applies - the strikethrough is hidden otherwise.
  wasPrice?: number;
};

export type PriceContext = {
  isMonthlyPolicy?: boolean;
  isDiscounted?: boolean;
};

export const EUROPEAN_RESCUE_PRODUCT_CODE = 'EuropeanRescue';

export type MonthlyContext = {
  // The member is already on a monthly long term policy - frequency is fixed.
  isMonthlyLongerTermPolicy?: boolean;
  // The member has taken up the Monthly Flex offer.
  monthlyFlex?: boolean;
};

/**
 * Whether one add-on is quoted monthly or annually. Everything on the quote follows the
 * policy's payment frequency, with one exception: European Rescue cannot be held alongside
 * Monthly Flex, so on a Flex quote it is priced annually - that is what the member would
 * actually pay, since selecting it forces the quote back to annual. A Monthly LTP policy
 * carries no such restriction, so European Rescue is a normal monthly add-on there.
 */
export const isAddOnPricedMonthly = (
  productCode: string | undefined,
  { isMonthlyLongerTermPolicy, monthlyFlex }: MonthlyContext,
): boolean => {
  if (productCode === EUROPEAN_RESCUE_PRODUCT_CODE) {
    return Boolean(isMonthlyLongerTermPolicy) && !monthlyFlex;
  }
  return Boolean(isMonthlyLongerTermPolicy || monthlyFlex);
};

/**
 * Picks the price to display for one priced thing on the quote. The backend sends all
 * four variants and leaves it to us to choose: monthly or annual, discounted or not.
 * Extra vehicles, extra people and paid upgrades all resolve identically, so the
 * choice is made once here rather than repeated per call site.
 */
export const resolveAddOnPrice = (
  details: AddOnPriceDetails | undefined,
  { isMonthlyPolicy, isDiscounted }: PriceContext,
): ResolvedAddOnPrice => {
  if (isMonthlyPolicy && isDiscounted) {
    return { nowPrice: details?.monthlyDiscountedPrice ?? 0, wasPrice: details?.monthlyPrice };
  }
  if (isMonthlyPolicy) {
    return { nowPrice: details?.monthlyPrice ?? 0 };
  }
  if (isDiscounted) {
    return { nowPrice: details?.discountedPrice ?? 0, wasPrice: details?.price };
  }
  return { nowPrice: details?.price ?? 0 };
};

/**
 * Resolves the effective prices for a paid upgrade given how many extra units - added
 * vehicles or added people - are on the quote. Both scale an upgrade identically, and a
 * quote never offers both, so they share one count. When the upgrade scales, the matching
 * entry in "additionalUnitPrices" is used; otherwise (product doesn't scale, or nothing
 * added yet) it falls back to the flat price fields.
 */
export const resolveUpgradePricing = (upgrade: PaidUpgrade, additionalUnits = 0): UpgradePricing => {
  const tier = upgrade.additionalUnitPrices?.find((entry) => entry.additionalUnits === additionalUnits);
  const { price, discountedPrice, monthlyPrice, monthlyDiscountedPrice } = tier ?? upgrade;
  return { price, discountedPrice, monthlyPrice, monthlyDiscountedPrice };
};

const calculateBasketPricing = (
  selected: PaidUpgrade[],
  monthly: boolean,
  pageModel: PageModel | null,
  vehiclesAdded = 0,
  peopleAdded = 0,
  removedExtras: string[] = [],
): BasketPricing => {
  const {
    basketContent, discountModel, extraPersonDetails, extraVehicleDetails, price, addonsContent,
  } = pageModel || {};

  const isDiscounted = discountModel?.isDiscountedPrice;
  const priceContext = { isMonthlyPolicy: monthly, isDiscounted };

  // Added vehicles and added people both shift an upgrade's unit tier, and a quote never
  // offers both, so one of the two is always zero.
  const upgradePrices = selected
    .map((upgrade) => resolveUpgradePricing(upgrade, vehiclesAdded + peopleAdded))
    .map((upgrade) => resolveAddOnPrice(upgrade, priceContext));
  const vehiclePrice = resolveAddOnPrice(extraVehicleDetails, priceContext);
  const personPrice = resolveAddOnPrice(extraPersonDetails, priceContext);

  /**
   * A removed extra is a held extra the member is dropping, so its price is subtracted from
   * the base "price" total rather than never added like a not-yet-taken add-on. Resolved via
   * the same picker as everything else on the quote, so the figure taken off is always the
   * one the backend supplied for the member's payment frequency - never derived here.
   */
  const removedPrices = (addonsContent ?? [])
    .filter((item) => item.productCode && removedExtras.includes(item.productCode))
    .map((item) => resolveAddOnPrice(item, priceContext));

  const totalPrice = (monthly ? basketContent?.monthlyHeadlinePriceNow : price) ?? 0;
  const addOnTotal = sumBy(upgradePrices, 'nowPrice')
    + vehiclesAdded * vehiclePrice.nowPrice
    + peopleAdded * personPrice.nowPrice
    - sumBy(removedPrices, 'nowPrice');

  /**
   * "wasTotal" stays null unless a discount applies - the render sites read it for
   * truthiness to decide whether to strike a price through, so computing it
   * unconditionally would put a bogus strikethrough on every undiscounted basket.
   * Every "wasPrice" needs its own "?? 0": unlike nowPrice the resolver returns it
   * raw, and a discounted quote whose payload omits the full price would otherwise
   * make this NaN, which renders as "you're saving £NaN".
   */
  let wasTotal: number | null = null;
  if (isDiscounted) {
    const wasHeadline = (monthly ? basketContent?.monthlyHeadlinePriceWas : discountModel?.originalPrice) ?? 0;
    wasTotal = sumBy(upgradePrices, (upgrade) => upgrade.wasPrice ?? 0)
      + vehiclesAdded * (vehiclePrice.wasPrice ?? 0)
      + peopleAdded * (personPrice.wasPrice ?? 0)
      + wasHeadline
      - sumBy(removedPrices, (removed) => removed.wasPrice ?? 0);
  }

  return { totalPrice, addOnTotal, wasTotal };
};

export default calculateBasketPricing;
